import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../context/AuthContext";

export default function TwoFactor() {
  const location = useLocation();
  const navigate = useNavigate();
  const { verifyTwoFactor } = useAuth();
  const data = location.state;
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!data?.temp_token) {
    navigate("/login", { replace: true });
    return null;
  }

  const isSetup = data.status === "2fa_setup_required";

  const submit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    try {
      await verifyTwoFactor(data.temp_token, code, trust);
      navigate("/workspace", { replace: true });
    } catch (err) {
      toast.error(err.message || "Invalid code");
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
      <div className="w-full max-w-sm sgp-card p-6 space-y-5">
        <div className="flex flex-col items-center text-center gap-2">
          <ShieldCheck className="h-8 w-8 text-teal-600" />
          <h1 className="font-heading font-bold text-lg">
            {isSetup ? "Set up your authenticator app" : "Enter your authentication code"}
          </h1>
          {isSetup ? (
            <p className="text-xs text-slate-500">
              Scan this QR with Google Authenticator / Authy, then enter the 6-digit code it shows.
            </p>
          ) : (
            <p className="text-xs text-slate-500">Open your authenticator app and enter the current 6-digit code.</p>
          )}
        </div>

        {isSetup && data.qr_png_base64 && (
          <div className="flex justify-center">
            <img
              src={`data:image/png;base64,${data.qr_png_base64}`}
              alt="Scan with your authenticator app"
              className="h-40 w-40 rounded-lg border border-slate-200"
            />
          </div>
        )}
        {isSetup && data.otpauth_uri && (
          <p className="text-[10px] text-slate-400 text-center break-all">
            Can't scan? Manual key is in the QR code URI, or contact Admin.
          </p>
        )}

        <form onSubmit={submit} className="space-y-4" data-testid="twofactor-form">
          <div className="flex justify-center">
            <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="twofactor-code">
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <InputOTPSlot key={i} index={i} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600 justify-center">
            <Checkbox checked={trust} onCheckedChange={setTrust} /> Trust this computer for 30 days
          </label>
          <button
            type="submit" disabled={busy || code.length !== 6} data-testid="twofactor-submit"
            className="w-full h-10 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold disabled:opacity-60 transition-colors"
          >
            {busy ? "Verifying…" : "Verify & continue"}
          </button>
        </form>
      </div>
    </div>
  );
}
